import axios from "axios"
import { supabase } from "../supabaseClient"

const api = axios.create({
  baseURL: process.env.REACT_APP_SERVER_URL ? `${process.env.REACT_APP_SERVER_URL}/api/email` : "/api/email",
  headers: { "Content-Type": "application/json" },
})

api.interceptors.request.use(
  async (config) => {
    const {
      data: { session },
    } = await supabase.auth.getSession()
    if (session) {
      config.headers["user-id"] = session.user.id
    }

    // Check if admin has selected a company
    try {
      const selectedCompanyName = localStorage.getItem("selectedCompanyName")
      if (selectedCompanyName) {
        config.headers["x-company-name"] = selectedCompanyName
      }
    } catch (e) {
      // Silently fail if localStorage access fails
    }

    return config
  },
  (error) => Promise.reject(error),
)

export interface EmailAddress {
  id: number
  company_name: string
  email_address: string // Decrypted by backend, never shows raw tokens

  // OAuth fields (metadata only, no sensitive data)
  oauth_provider?: string
  token_type?: string
  scopes?: string
  token_expiry?: string

  // Gmail identifiers (non-sensitive)
  gmail_history_id?: string
  watch_expiration?: string

  // Assignment / status
  assigned_agent_id: string | null
  is_connected?: boolean
  is_active?: boolean
  connected_at?: string
  last_sync_at?: string
  last_error?: string

  created_at?: string
  updated_at?: string
  deleted_at?: string
}

export const emailAddressService = {
  list: async (companyName?: string): Promise<EmailAddress[]> => {
    const url = companyName ? `/email-addresses?company_name=${encodeURIComponent(companyName)}` : "/email-addresses"
    const response = await api.get(url)
    return response.data
  },

  create: async (data: Partial<EmailAddress>): Promise<EmailAddress> => {
    const response = await api.post("/email-addresses", data)
    return response.data
  },

  update: async (id: number, data: Partial<EmailAddress>): Promise<EmailAddress> => {
    const response = await api.patch(`/email-addresses/${id}`, data)
    return response.data
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/email-addresses/${id}`)
  },
}
